import { Col, Form, Row, Spinner } from 'react-bootstrap'
import React, { useEffect } from 'react'
import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectCreateNewCRMContact,
  selectExternalId,
  selectIsInitializing,
  selectIsLoadingContact,
  selectShouldCreateContact,
  selectShowLinkContact,
  setContact,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
  setIsInitializing,
  setNewContactData,
  setShouldCreateContact,
  setShouldLinkContact,
} from '../../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { Button } from '../../../components/Button/Button'
import {
  getIntegrationDisplayName,
  IntegrationServiceId,
  canCreateContactForUser,
  getRecordTypeName,
  integrationRequiresOwnerSelection,
  isContactCreateEnabledInConfig,
  isContactUpdateEnabledInConfig,
} from '../../../common/integrations'
import { useAccount } from '../../../common/useAccount'
import { getEffectiveAccountIntegrationConfig } from '../../../models/Account'
import { useIntegrations } from '../../Integration/useIntegrations'
import { User } from '../../../models/User'
import { useFetchContact } from '../hooks'
import { useSaveIntegrationServiceConfig } from '../../Integration/useSaveIntegrationServiceConfig'
import { UpdateExternalContactCreateSection } from './UpdateExternalContactCreateSection'
import { UpdateExternalContactLinkSection } from './UpdateExternalContactLinkSection'
import { type ClicFpCodeMetadata } from '../models'

type ContactSelectStepProps = {
  serviceId: IntegrationServiceId
  onCancel: () => void
  user: User
}

type WealthContactCreateMetadata = {
  sendInvite: boolean
}

type ContactCreateMetadata = WealthContactCreateMetadata | ClicFpCodeMetadata

type ContactSelectFormValue = {
  createNewContact: boolean
  firstName?: string
  lastName?: string
  email?: string
  name?: string
  selectedExternalId?: string
  createNewZocksContact: boolean
  linkToExistingZocksContact: boolean
  metadata?: ContactCreateMetadata
  ownerId?: string
  serviceConfig?: Record<string, unknown>
}

export const UpdateExternalContactSelectStep: React.FC<ContactSelectStepProps> = ({ serviceId, onCancel, user }) => {
  const dispatch = useAppDispatch()
  const isLoadingContact = useAppSelector(selectIsLoadingContact)

  const createNewCRMContact = useAppSelector(selectCreateNewCRMContact)

  const shouldCreateContact = useAppSelector(selectShouldCreateContact)
  const shouldLinkContact = useAppSelector(selectShowLinkContact)
  const isInitializing = useAppSelector(selectIsInitializing)
  const externalId = useAppSelector(selectExternalId)
  const contact = useAppSelector(selectContact)

  const form = useForm<ContactSelectFormValue>()
  const [createNewContact, ownerId] = useWatch({
    control: form.control,
    name: ['createNewContact', 'ownerId'],
  })

  const requiresOwner = integrationRequiresOwnerSelection(serviceId)

  const { account } = useAccount()
  const { supportsContactUpdate, isLoading: isIntegrationsLoading } = useIntegrations()
  const { saveServiceConfig } = useSaveIntegrationServiceConfig(serviceId)

  const integrationConfig = getEffectiveAccountIntegrationConfig(account, serviceId)
  const createEnabled = isContactCreateEnabledInConfig(integrationConfig)
  const updateEnabled = isContactUpdateEnabledInConfig(integrationConfig)

  const supportsCreateContact = canCreateContactForUser({
    serviceId,
    userType: user.type,
  })
  const createOnly = supportsCreateContact && !supportsContactUpdate(serviceId)

  const handleContinue = (formData: ContactSelectFormValue) => {
    if (formData.createNewContact && formData.serviceConfig) {
      saveServiceConfig(formData.serviceConfig)
    }
    dispatch(setCreateNewCRMContact(formData.createNewContact))
    dispatch(setShouldLinkContact(formData.linkToExistingZocksContact))
    const createNewZocksContact = formData.createNewContact ? formData.createNewZocksContact : shouldCreateContact
    dispatch(setShouldCreateContact(createNewZocksContact))
    if (formData.createNewContact) {
      dispatch(
        setNewContactData({
          firstName: formData.firstName,
          lastName: formData.lastName,
          name: formData.name,
          email: formData.email,
          metadata: formData.metadata,
          ownerId: formData.ownerId,
        }),
      )
    }
    if (!formData.createNewContact && formData.email && contact) {
      dispatch(setContact({ ...contact, email: formData.email }))
    }
    dispatch(setIsInitializing(false))
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
  }
  const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)

  const { searchForEmail } = useFetchContact({ serviceId })

  useEffect(() => {
    if (isIntegrationsLoading) {
      return
    }

    form.reset({
      createNewContact: createOnly || (supportsCreateContact && createEnabled && (createNewCRMContact || !updateEnabled)),
      createNewZocksContact: shouldCreateContact,
      linkToExistingZocksContact: shouldLinkContact,
    })
  }, [
    createNewCRMContact,
    shouldCreateContact,
    shouldLinkContact,
    supportsCreateContact,
    createOnly,
    createEnabled,
    updateEnabled,
    form,
    isIntegrationsLoading,
  ])

  useEffect(() => {
    if (isIntegrationsLoading) {
      return
    }

    if (createNewContact || createOnly) {
      dispatch(setContact(null))
      dispatch(setExternalId(null))
      dispatch(setIsInitializing(false))
      return
    }
    if (!externalId && user.email) {
      searchForEmail(user.email)
    } else {
      dispatch(setIsInitializing(false))
    }
  }, [createNewContact, createOnly, dispatch, user.email, searchForEmail, externalId, isIntegrationsLoading])

  if (isInitializing) {
    return (
      <div className='d-flex align-items-center justify-content-center mb-3'>
        <Spinner />
      </div>
    )
  }

  const integrationEntityName = getRecordTypeName(serviceId)

  const isContactMissing = !contact && !createNewContact
  const isOwnerMissing = createNewContact && requiresOwner && !ownerId
  const isSelectedActionEnabled = createNewContact ? createEnabled : updateEnabled
  const isContinueDisabled = isLoadingContact || isContactMissing || isOwnerMissing || !isSelectedActionEnabled

  return (
    <FormProvider {...form}>
      <div className='vh-100 ms-3 me-3 d-flex flex-column'>
        <div className='flex-grow-1 overflow-hidden'>
          <Row className='mb-4'>
            <Col>
              {createOnly
                ? `Create new ${integrationEntityName} in ${integrationDisplayName}`
                : `Choose how to save this ${integrationEntityName} to ${integrationDisplayName}`}
            </Col>
          </Row>
          {supportsCreateContact && !createOnly ? (
            <Row>
              <Col>
                <Controller
                  name='createNewContact'
                  control={form.control}
                  render={({ field }) => (
                    <Form.Check
                      inline
                      type='radio'
                      name='createNewContact-group'
                      className='pb-2'
                      label={`Create new ${integrationEntityName} in ${integrationDisplayName}`}
                      id='create-new-contact'
                      checked={field.value === true}
                      disabled={isLoadingContact || !createEnabled}
                      onChange={() => {
                        field.onChange(true)
                      }}
                    />
                  )}
                />
                {!createEnabled ? <div className='paragraph-small zocks-text-gray-600'>Turned off by your account administrator</div> : null}
              </Col>
            </Row>
          ) : null}
          {supportsCreateContact && createNewContact ? <UpdateExternalContactCreateSection serviceId={serviceId} /> : null}
          {!createOnly ? (
            <Row className='mt-3'>
              <Col>
                <Controller
                  name='createNewContact'
                  control={form.control}
                  render={({ field }) => (
                    <Form.Check
                      inline
                      type='radio'
                      className='pb-2'
                      name='createNewContact-group'
                      label={` Update existing ${integrationEntityName} in ${integrationDisplayName}`}
                      id='link-to-contact'
                      checked={field.value === false}
                      value={'link-to-contact'}
                      disabled={isLoadingContact || !updateEnabled}
                      onChange={() => {
                        field.onChange(false)
                      }}
                    />
                  )}
                />
                {!updateEnabled ? <div className='paragraph-small zocks-text-gray-600'>Turned off by your account administrator</div> : null}
              </Col>
              {!createNewContact ? <UpdateExternalContactLinkSection serviceId={serviceId} /> : null}
            </Row>
          ) : null}
        </div>

        <div className='flex-shrink-0 pt-3'>
          <Row className='justify-content-end mb-4'>
            <Col xs='auto'>
              <Button className='mt-2 me-auto' variant='secondary' onClick={onCancel}>
                Cancel
              </Button>
            </Col>
            <Col xs='auto'>
              <Button className='mt-2 me-auto' variant='primary' onClick={form.handleSubmit(handleContinue)} disabled={isContinueDisabled}>
                Continue
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </FormProvider>
  )
}
